// src/components/sidebar/RightSidebar.js
import React, { useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { FaTimes, FaChevronRight } from 'react-icons/fa';
import { AuthContext } from '../../context/AuthContext';
import { useSidebar } from '../../context/SidebarContext';
import { dashboardLayoutService } from '../../services/dashboardLayoutService';
import { cardRegistry } from '../../services/cardRegistry';
import { initializeCards } from '../../services/cardInitializer';
import BaseCard from '../dashboard/base/BaseCard';
import useHeaderConfig from '../../hooks/useHeaderConfig';
import BannerSlider from '../BannerSlider';
import { bannerService } from '../../services/bannerService';
import logger from '../../utils/logger';

const RightSidebar = React.memo(({ headerHeight = 0 }) => {
    const { user, hasCardPermission, isSuperuser, userRoles, isAuthenticated, cardPermissions, cardPermsLoading } = useContext(AuthContext);
    const {
        rightVisible,
        rightWidth,
        toggleRightSidebar,
        handleRightMouseDown,
        isMobile
    } = useSidebar();

    const { hasHeader } = useHeaderConfig();
    const [sidebarCards, setSidebarCards] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);
    const skipNextApiSyncRef = useRef(false);
    const [banners, setBanners] = useState([]);
    const [bannerLocationSettings, setBannerLocationSettings] = useState(null);

    // Use refs to avoid infinite loops from function reference changes
    const hasCardPermissionRef = useRef(hasCardPermission);
    useEffect(() => {
        hasCardPermissionRef.current = hasCardPermission;
    }, [hasCardPermission]);

    const mobileBaseTop = hasHeader ? 56 : 0;
    const mobileFullTop = hasHeader ? (headerHeight || 56) : 0;
    const [mobileTopOffset, setMobileTopOffset] = useState(mobileFullTop);

    useEffect(() => {
        if (!isMobile) return;
        if (!hasHeader) {
            setMobileTopOffset(0);
            return;
        }

        setMobileTopOffset(mobileFullTop);

        const handleScroll = () => {
            const scrollY = window.scrollY;
            const newTop = Math.max(mobileBaseTop, mobileFullTop - scrollY);
            setMobileTopOffset(newTop);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [isMobile, hasHeader, mobileBaseTop, mobileFullTop]);

    // Always show sidebar on all pages for logged-in users
    const shouldShowSidebar = useCallback(() => {
        if (!user) return false;
        return true;
    }, [user]);

    // Store shouldShowSidebar in ref to avoid dependency cycle
    const shouldShowSidebarRef = useRef(shouldShowSidebar);
    useEffect(() => {
        shouldShowSidebarRef.current = shouldShowSidebar;
    }, [shouldShowSidebar]);

    // Fetch banners for right_sidebar location
    useEffect(() => {
        const fetchBanners = async () => {
            if (!user) return;
            try {
                const result = await bannerService.getActiveBanners('right_sidebar');
                if (result.success) {
                    const bannersData = result.banners || result.data || [];
                    setBanners(bannersData);
                    if (result.locationSettings) {
                        setBannerLocationSettings(result.locationSettings);
                    }
                }
            } catch (err) {
                logger.error('[RightSidebar] Error fetching banners:', err);
            }
        };
        fetchBanners();
    }, [user]);

    // Create dashboardId same way as DashboardPage - memoized
    const dashboardId = useMemo(() => {
        let roleKey = 'default';
        if (isSuperuser) {
            roleKey = 'superuser';
        } else if (userRoles && userRoles.length > 0) {
            roleKey = userRoles.sort().join('-');
        }
        return `dashboard-${roleKey}`;
    }, [isSuperuser, userRoles]);

    // Load cards enabled for sidebar
    const loadSidebarCards = useCallback(async () => {
        logger.log('[RightSidebar] loadSidebarCards called, shouldShowSidebar:', shouldShowSidebarRef.current());

        if (!shouldShowSidebarRef.current()) {
            setLoading(false);
            return;
        }

        setLoading(true);

        try {
            // Make sure cardRegistry is initialized
            if (!cardRegistry.isInitialized()) {
                logger.log('[RightSidebar] Card registry not initialized yet, waiting...');
                return;
            }

            // Sync from API first to ensure we have the latest settings (unless we just saved locally)
            if (!skipNextApiSyncRef.current) {
                try {
                    await dashboardLayoutService.syncFromApi(dashboardId);
                } catch (syncError) {
                    logger.warn('[RightSidebar] Failed to sync from API, using localStorage:', syncError);
                }
            } else {
                skipNextApiSyncRef.current = false;
                logger.log('[RightSidebar] Skipping API sync, using fresh localStorage data');
            }

            // Get all cards from registry
            const allCards = cardRegistry.getAllCards();
            logger.log('[RightSidebar] All cards from registry:', allCards.length, allCards.map(c => ({ id: c.id, title: c.title })));

            // Get sidebar visibility settings - fresh load from localStorage (which may have been updated by syncFromApi)
            const sidebarVisibility = dashboardLayoutService.loadSidebarVisibility(dashboardId, {});
            
            logger.log('[RightSidebar] Loading sidebar cards:', {
                dashboardId,
                sidebarVisibility,
                allCardsCount: allCards.length
            });
            
            // Filter cards for sidebar with optimized permission checking
            const filteredCards = allCards.filter(card => {
                // Check if enabled for sidebar first (cheaper check)
                const isEnabled = sidebarVisibility[card.id] === true;
                if (!isEnabled) {
                    return false;
                }
                
                // Then check permissions only for enabled cards
                if (!isSuperuser && !hasCardPermissionRef.current(card.id)) {
                    return false;
                }

                return true;
            });

            logger.log('[RightSidebar] Filtered cards:', filteredCards.map(c => c.id));
            setSidebarCards(filteredCards);
            setLoading(false);
        } catch (error) {
            logger.error('[RightSidebar] Error loading cards:', error);
            setLoading(false);
        }
    }, [isSuperuser, dashboardId, isInitialized]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        loadSidebarCards();
    }, [loadSidebarCards]);

    useEffect(() => {
        if (isInitialized || cardRegistry.isInitialized()) {
            if (!isInitialized) {
                setIsInitialized(true);
            }
            return;
        }

        if (!isAuthenticated || (!isSuperuser && cardPermsLoading)) {
            return;
        }

        const doInit = async () => {
            try {
                if (cardRegistry.isInitialized()) {
                    setIsInitialized(true);
                    return;
                }

                let permittedCardIds = null;
                if (!isSuperuser && cardPermissions && typeof cardPermissions === 'object') {
                    permittedCardIds = [];
                    Object.entries(cardPermissions).forEach(([cardId, roles]) => {
                        if (Array.isArray(roles) && Array.isArray(userRoles) &&
                            userRoles.some(userRole => roles.includes(userRole))) {
                            permittedCardIds.push(cardId);
                        }
                    });
                }

                await initializeCards(isAuthenticated, permittedCardIds);
                logger.log('[RightSidebar] Card registry initialized by sidebar');
                setIsInitialized(true);
            } catch (error) {
                logger.error('[RightSidebar] Failed to initialize cards:', error);
            }
        };

        doInit();
    }, [isInitialized, isAuthenticated, isSuperuser, cardPermsLoading, cardPermissions, userRoles]);

    // Listen for custom sidebar update events - reload from localStorage only (not API) to avoid race conditions
    useEffect(() => {
        const handleSidebarUpdate = (event) => {
            logger.log('[RightSidebar] Sidebar settings updated, reloading cards from localStorage', event.detail);

            // Don't sync from API here - just read directly from localStorage
            // The DashboardSettings already saved to localStorage, so we just need to read it
            if (!cardRegistry.isInitialized()) {
                return;
            }

            // Set flag to skip API sync on next loadSidebarCards call
            skipNextApiSyncRef.current = true;

            const allCards = cardRegistry.getAllCards();
            const sidebarVisibility = dashboardLayoutService.loadSidebarVisibility(dashboardId, {});

            const filteredCards = allCards.filter(card => {
                const isEnabled = sidebarVisibility[card.id] === true;
                if (!isEnabled) return false;
                if (!isSuperuser && !hasCardPermissionRef.current(card.id)) return false;
                return true;
            });

            logger.log('[RightSidebar] Updated sidebar cards:', filteredCards.map(c => c.id));
            setSidebarCards(filteredCards);
            setLoading(false);
        };

        window.addEventListener('sidebarSettingsUpdate', handleSidebarUpdate);
        return () => window.removeEventListener('sidebarSettingsUpdate', handleSidebarUpdate);
    }, [dashboardId, isSuperuser]);

    // Remove the periodic check - rely on event-based updates only
    // useEffect(() => {
    //     const interval = setInterval(() => {
    //         loadSidebarCards();
    //     }, 2000); // Check every 2 seconds
    //
    //     return () => clearInterval(interval);
    // }, [loadSidebarCards]);

    // Auto-show sidebar when cards are available (separate effect to prevent loops)
    useEffect(() => {
        if (sidebarCards.length > 0 && 
            localStorage.getItem('rightSidebarManuallyToggled') !== 'true' && 
            !rightVisible) {
            logger.log('[RightSidebar] Auto-showing sidebar with cards');
            toggleRightSidebar(false); // Show sidebar
        }
    }, [sidebarCards.length, rightVisible, toggleRightSidebar]);

    // Track manual toggles
    const handleToggle = () => {
        localStorage.setItem('rightSidebarManuallyToggled', 'true');
        toggleRightSidebar();
    };

    // Don't render if shouldn't show
    if (!shouldShowSidebar()) return null;

    return (
        <>
            {/* Mobile overlay */}
            {isMobile && rightVisible && (
                <div 
                    className="fixed inset-0 bg-black bg-opacity-50"
                    style={{ zIndex: 44 }}
                    onClick={handleToggle}
                />
            )}
            
            {/* Right Sidebar - Under header */}
            <aside
                className={`fixed right-0 bg-white shadow-lg transition-transform duration-300 overflow-hidden ${isMobile ? 'mobile-sidebar-right' : ''}
                    ${rightVisible ? 'translate-x-0' : 'translate-x-full'}`}
                style={{ 
                    width: `${rightWidth}px`,
                    top: isMobile ? `${mobileTopOffset}px` : `${headerHeight}px`,
                    bottom: 0,
                    zIndex: 45 // Higher than content (40) but lower than header (50)
                }}
            >
                {/* Header */}
                <div className="bg-gray-100 px-4 py-3 flex justify-between items-center border-b">
                    <h5 className="font-semibold text-gray-800">Quick Dashboard</h5>
                    <button
                        className="p-1 rounded hover:bg-gray-200 text-gray-600"
                        onClick={handleToggle}
                    >
                        {isMobile ? <FaTimes /> : <FaChevronRight/>}
                    </button>
                </div>

                {/* Banner Section */}
                {banners.length > 0 && (
                    <div className="p-2 border-b">
                        <BannerSlider
                            banners={banners}
                            locationSettings={bannerLocationSettings}
                            autoPlay={true}
                            interval={5000}
                            showOnMobile={true}
                            isMobile={isMobile}
                            className="rounded-lg"
                        />
                    </div>
                )}

                {/* Cards Container */}
                <div className="p-4 overflow-y-auto" style={{ height: banners.length > 0 ? 'calc(100% - 200px)' : 'calc(100% - 60px)' }}>
                    {loading ? (
                        <div className="text-center py-8">
                            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
                            <p className="mt-2 text-gray-500">Loading cards...</p>
                        </div>
                    ) : sidebarCards.length === 0 ? (
                        <div className="text-center py-8">
                            <p className="text-gray-500">No cards activated for sidebar</p>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {sidebarCards.map(card => {
                                const CardComponent = card.component;
                                return (
                                    <div key={card.id} className="bg-gray-50 rounded-lg shadow-sm">
                                        {CardComponent ? (
                                            <CardComponent
                                                id={card.id}
                                                title={card.title}
                                                user={user}
                                                compactView={true}
                                                editMode={false}
                                            />
                                        ) : card.content ? (
                                            <BaseCard
                                                title={card.title}
                                                cardId={card.id}
                                                size="small"
                                            >
                                                {card.content}
                                            </BaseCard>
                                        ) : (
                                            <BaseCard
                                                title={card.title}
                                                cardId={card.id}
                                                size="small"
                                            >
                                                <div className="p-4 text-gray-500">
                                                    No component defined
                                                </div>
                                            </BaseCard>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                
                {/* Resize handle */}
                {!isMobile && (
                    <div
                        className="absolute top-0 left-0 w-px h-full cursor-col-resize bg-gray-200 hover:bg-primary transition-colors"
                        onMouseDown={handleRightMouseDown}
                    />
                )}
            </aside>
            
        </>
    );
});

// Add display name for debugging
RightSidebar.displayName = 'RightSidebar';

export default RightSidebar;